<script>
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { Chrome } from '@lk77/vue3-color';
import InboxesAPI from 'dashboard/api/inboxes';
import WidgetPreviewHost from './components/WidgetPreviewHost.vue';
import {
  getWidgetLocaleMessages,
  readWidgetLocaleText,
} from 'dashboard/helper/widgetLocale';

export default {
  name: 'WelcomeScreen',
  components: { NextButton, Chrome, WidgetPreviewHost },
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      // LiveChat-like accordion state
      activeSection: 'customize_header',
      isDark: document.body.classList.contains('dark'),
      darkObserver: null,

      // Persisted config lives in channel_web_widgets.widget_theme_config
      widgetThemeConfig: {},
      // Form state (welcome screen)
      showWelcomeScreen: true,
      showLogo: true,
      logoFile: null,
      logoUrl: '',
      welcomeTitle: '',
      welcomeSubtitle: '',
      homeCardMessageOnline: '',
      homeCardMessageOffline: '',
      socialLinks: [], // [{ type: 'whatsapp'|'telegram'|..., url: '' }]
      directLinks: [], // [{ label: '', url: '' }]

      // Simple per-welcome colors (optional)
      welcomeLinkCardBg: '#ffffff',
      welcomeLinkCardText: '#111111',
      welcomeLinkIcon: '#6e6e6e',

      // Preview iframe state
      previewDocHtml: '',

      // Color picker popover
      isColorPopoverOpen: false,
      activeColorPickerTarget: null,
      activeColorPickerValue: '#000000',
      colorPopoverTop: 0,
      colorPopoverLeft: 0,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    widgetThemePreset() {
      return this.inbox.widget_theme_preset || 'classic';
    },
    widgetColor() {
      return this.inbox.widget_color || '#0059e1';
    },
    widgetLocaleMessages() {
      return getWidgetLocaleMessages(this.inbox.widget_language);
    },
    welcomeTitlePlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'START_NEW_CONVERSATION',
        'Selamat datang! Tulis kepada kami'
      );
    },
    welcomeSubtitlePlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'TEAM_AVAILABILITY.BACK_AS_SOON_AS_POSSIBLE',
        'Short tagline'
      );
    },
    homeCardOnlinePlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'TEAM_AVAILABILITY.ONLINE',
        'Shown when agents are online'
      );
    },
    homeCardOfflinePlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'TEAM_AVAILABILITY.OFFLINE',
        'Shown when agents are away/offline'
      );
    },
    directLinkLabelPlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'PRE_CHAT_FORM.FIELDS.MESSAGE.LABEL',
        'Label'
      );
    },
    saveButtonLabel() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'COMPONENTS.FORM_BUBBLE.SUBMIT',
        'Save'
      );
    },
    welcomeHeading() {
      return this.inbox.welcome_title || '';
    },
    welcomeTagline() {
      return this.inbox.welcome_tagline || '';
    },
    liveChatwootSettings() {
      const isMidnight = this.widgetThemePreset === 'midnight';

      const themeConfig = {
        ...(this.widgetThemeConfig || {}),
        // Welcome screen config (used by widget Home.vue)
        welcomeScreenEnabled: !!this.showWelcomeScreen,
        welcomeShowLogo: !!this.showLogo,
        welcomeHeaderLogoUrl: this.logoFile
          ? this.logoUrl || ''
          : this.widgetThemeConfig?.welcomeHeaderLogoUrl || '',
        homeCardMessageOnline: this.homeCardMessageOnline || '',
        homeCardMessageOffline: this.homeCardMessageOffline || '',
        welcomeSocialLinks: this.socialLinks || [],
        welcomeDirectLinks: this.directLinks || [],
        welcomeLinkCardBg: this.welcomeLinkCardBg,
        welcomeLinkCardText: this.welcomeLinkCardText,
        welcomeLinkIcon: this.welcomeLinkIcon,
      };

      return {
        locale: this.inbox.widget_language || 'en',
        position: this.inbox.position || 'right',
        type: 'standard',
        // Preview must be able to render unsaved theme changes from host settings.
        allowThemeOverride: true,
        darkMode: isMidnight ? 'dark' : 'light',
        themePreset: this.widgetThemePreset,
        widgetStyle: 'rounded',
        widgetColor: this.widgetColor,
        iconColor: '#ffffff',
        themeConfig,
        welcomeTitle: this.welcomeTitle || this.welcomeHeading,
        welcomeDescription: this.welcomeSubtitle || this.welcomeTagline,
        announcementText: this.inbox.announcement_text || '',
        announcementUrl: this.inbox.announcement_url || '',
        announcementBgColor: this.inbox.announcement_bg_color || '#242529',
        announcementTextColor: this.inbox.announcement_text_color || '#ffffff',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        initialRoute: 'home',
        previewScene: 'home',
        previewSceneData: {},
      };
    },
  },
  watch: {
    inbox() {
      this.loadFromInbox();
      this.previewDocHtml = this.buildPreviewDoc();
    },
    showWelcomeScreen() {
      this.pushSettingsToPreview();
    },
    showLogo() {
      this.pushSettingsToPreview();
    },
    homeCardMessageOnline() {
      this.pushSettingsToPreview();
    },
    homeCardMessageOffline() {
      this.pushSettingsToPreview();
    },
    welcomeTitle() {
      this.pushSettingsToPreview();
    },
    welcomeSubtitle() {
      this.pushSettingsToPreview();
    },
    logoUrl() {
      this.pushSettingsToPreview();
    },
    socialLinks: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
    directLinks: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
    welcomeLinkCardBg() {
      this.pushSettingsToPreview();
    },
    welcomeLinkCardText() {
      this.pushSettingsToPreview();
    },
    welcomeLinkIcon() {
      this.pushSettingsToPreview();
    },
  },
  mounted() {
    this.loadFromInbox();
    this.previewDocHtml = this.buildPreviewDoc();
    document.addEventListener('click', this.handleGlobalClick);
    this.darkObserver = new MutationObserver(() => {
      this.isDark = document.body.classList.contains('dark');
    });
    this.darkObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick);
    this.darkObserver?.disconnect();
  },
  methods: {
    loadFromInbox() {
      const cfg = this.inbox.widget_theme_config || {};
      this.widgetThemeConfig = { ...cfg };
      this.showWelcomeScreen = cfg.welcomeScreenEnabled ?? true;
      this.showLogo = cfg.welcomeShowLogo ?? true;
      const legacyCardMessage = cfg.homeCardMessage || '';
      this.homeCardMessageOnline =
        cfg.homeCardMessageOnline || legacyCardMessage || '';
      this.homeCardMessageOffline =
        cfg.homeCardMessageOffline || legacyCardMessage || '';
      this.socialLinks = Array.isArray(cfg.welcomeSocialLinks)
        ? cfg.welcomeSocialLinks
        : [];
      this.directLinks = Array.isArray(cfg.welcomeDirectLinks)
        ? cfg.welcomeDirectLinks
        : [];
      this.welcomeLinkCardBg = cfg.welcomeLinkCardBg || '#ffffff';
      this.welcomeLinkCardText = cfg.welcomeLinkCardText || '#111111';
      this.welcomeLinkIcon = cfg.welcomeLinkIcon || '#6e6e6e';

      this.logoUrl = cfg.welcomeHeaderLogoUrl || this.inbox.avatar_url || '';
      this.welcomeTitle = this.inbox.welcome_title || '';
      this.welcomeSubtitle = this.inbox.welcome_tagline || '';

      // Ensure the social media editor always starts with at least 2 lines
      // (matches LiveChat UI density and user request for "one more line").
      const minSocialRows = 2;
      if (!Array.isArray(this.socialLinks)) this.socialLinks = [];
      if (this.socialLinks.length < minSocialRows) {
        const toAdd = minSocialRows - this.socialLinks.length;
        for (let i = 0; i < toAdd; i += 1) {
          this.socialLinks.push({ type: 'whatsapp', url: '' });
        }
      }
    },
    toggleSection(key) {
      this.activeSection = this.activeSection === key ? '' : key;
    },
    handleLogoUpload(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;
      this.logoFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.logoUrl = e?.target?.result || '';
        this.showLogo = true;
      };
      reader.readAsDataURL(file);
    },
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.widgetThemePreset === 'midnight';
      const bg =
        isMidnight && this.widgetThemeConfig?.background
          ? this.widgetThemeConfig.background
          : isMidnight
            ? '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);

      // runtime script tags to avoid "</"
      const SCRIPT_OPEN = '\x3cscript\x3e';
      const SCRIPT_CLOSE = '\x3c\x2fscript\x3e';

      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style id="wlab-welcome-style">` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        /* Bubble starts invisible; polling restores it when widget is closed */
        `.woot--bubble-holder{visibility:hidden;transition:visibility 0s;}` +
        `</style>` +
        `</head><body>` +
        SCRIPT_OPEN +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{locale:(d.s&&d.s.locale)||'en',showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);};` +
        `})(document,"script");` +
        SCRIPT_CLOSE +
        `</body></html>`
      );
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe || !iframe.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (e) {
        // ignore
      }
    },
    addSocialLink() {
      this.socialLinks = [...this.socialLinks, { type: 'whatsapp', url: '' }];
      this.pushSettingsToPreview();
    },
    removeSocialLink(idx) {
      this.socialLinks = this.socialLinks.filter((_, i) => i !== idx);
      this.pushSettingsToPreview();
    },
    addDirectLink() {
      this.directLinks = [...this.directLinks, { label: '', url: '' }];
      this.pushSettingsToPreview();
    },
    removeDirectLink(idx) {
      this.directLinks = this.directLinks.filter((_, i) => i !== idx);
      this.pushSettingsToPreview();
    },
    normalizeHex(value) {
      const raw = (value || '').toString().trim();
      const match = raw.match(/^#?([0-9a-f]{6})$/i);
      return match ? `#${match[1].toLowerCase()}` : '#000000';
    },
    openColorPicker(target, currentValue) {
      this.activeColorPickerTarget = target;
      this.activeColorPickerValue = this.normalizeHex(currentValue);
      this.isColorPopoverOpen = true;
      this.$nextTick(() => this.positionColorPopover());
    },
    positionColorPopover() {
      const triggerEl =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      const rect = triggerEl?.getBoundingClientRect?.();
      const popoverWidth = 230;
      const safeLeft = rect
        ? Math.min(
            Math.max(10, rect.left),
            Math.max(10, window.innerWidth - popoverWidth - 10)
          )
        : 20;
      this.colorPopoverLeft = Math.round(safeLeft);
      this.colorPopoverTop = Math.round((rect?.bottom || 20) + 8);
    },
    closeColorPicker() {
      this.isColorPopoverOpen = false;
      this.activeColorPickerTarget = null;
    },
    handleGlobalClick(event) {
      if (!this.isColorPopoverOpen) return;
      const popover = this.$refs.colorPopover;
      const target = event?.target;
      if (popover?.contains?.(target)) return;
      if (target?.closest?.('.color-swatch-btn')) return;
      this.closeColorPicker();
    },
    onColorPopoverUpdate(payload) {
      const hex = this.normalizeHex(payload?.hex || payload);
      const t = this.activeColorPickerTarget;
      if (t === 'welcomeLinkCardBg') this.welcomeLinkCardBg = hex;
      if (t === 'welcomeLinkCardText') this.welcomeLinkCardText = hex;
      if (t === 'welcomeLinkIcon') this.welcomeLinkIcon = hex;
      this.activeColorPickerValue = hex;
      this.pushSettingsToPreview();
    },
    openWebsitePreview() {
      const url =
        this.inbox.website_url || this.inbox.channel?.website_url || '';
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async save() {
      try {
        if (this.logoFile && this.inbox?.id) {
          await InboxesAPI.uploadWelcomeHeaderLogo(this.inbox.id, this.logoFile);
          await this.$store.dispatch('inboxes/get');
          const refreshedInbox =
            this.$store.getters['inboxes/getInbox'](this.inbox.id) || this.inbox;
          const refreshedConfig = refreshedInbox.widget_theme_config || {};
          this.widgetThemeConfig = { ...refreshedConfig };
          this.logoUrl =
            refreshedConfig.welcomeHeaderLogoUrl || refreshedInbox.avatar_url || '';
          this.logoFile = null;
        }

        const channelConfig = {
          ...(this.inbox.widget_theme_config || {}),
          ...(this.widgetThemeConfig || {}),
          welcomeScreenEnabled: !!this.showWelcomeScreen,
          welcomeShowLogo: !!this.showLogo,
          homeCardMessageOnline: this.homeCardMessageOnline || '',
          homeCardMessageOffline: this.homeCardMessageOffline || '',
          welcomeSocialLinks: this.socialLinks || [],
          welcomeDirectLinks: this.directLinks || [],
          welcomeLinkCardBg: this.welcomeLinkCardBg,
          welcomeLinkCardText: this.welcomeLinkCardText,
          welcomeLinkIcon: this.welcomeLinkIcon,
        };

        const payload = {
          id: this.inbox.id,
          channel: {
            welcome_title: this.welcomeTitle || '',
            welcome_tagline: this.welcomeSubtitle || '',
            widget_theme_config: channelConfig,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert('Welcome screen saved');
      } catch (e) {
        useAlert('Unable to save welcome screen');
      }
    },
  },
};
</script>

<template>
  <div
    class="widget-builder-page ws-scroll-layout"
    :class="{ 'is-dark': isDark }"
  >
    <div
      v-if="isColorPopoverOpen"
      ref="colorPopover"
      class="widget-color-popover"
      :style="{ top: `${colorPopoverTop}px`, left: `${colorPopoverLeft}px` }"
    >
      <Chrome
        disable-alpha
        :model-value="activeColorPickerValue"
        class="widget-color-popover__chrome"
        @update:model-value="onColorPopoverUpdate"
      />
    </div>

    <div class="widget-builder-layout">
      <section class="builder-config-panel">
        <header class="builder-header">
          <div class="builder-title">Welcome screen</div>
        </header>

        <div class="builder-sections">
          <p class="builder-desc">
            Configure the widget home (start conversation) page.
          </p>
          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'customize_header'"
              @click="toggleSection('customize_header')"
            >
              <span class="section-left">
                <span class="i-lucide-image section-icon" />
                <span class="section-name">Customize header</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div
              v-if="activeSection === 'customize_header'"
              class="section-content"
            >
              <label class="switch-row">
                <input v-model="showLogo" type="checkbox" />
                <span>Show logo</span>
              </label>
              <div class="upload-row">
                <div class="upload-preview">
                  <img v-if="logoUrl" :src="logoUrl" alt="" />
                  <div v-else class="upload-placeholder">Logo</div>
                </div>
                <label class="upload-btn">
                  Upload
                  <input
                    class="hidden-upload"
                    type="file"
                    accept="image/*"
                    @change="handleLogoUpload"
                  />
                </label>
              </div>
              <div class="option-block">
                <p class="option-title">Welcome text</p>
                <input
                  v-model="welcomeTitle"
                  class="manual-input manual-input--full"
                  :placeholder="welcomeTitlePlaceholder"
                  spellcheck="false"
                />
              </div>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'manage_content'"
              @click="toggleSection('manage_content')"
            >
              <span class="section-left">
                <span class="i-lucide-layout-panel-top section-icon" />
                <span class="section-name">Manage content</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div
              v-if="activeSection === 'manage_content'"
              class="section-content"
            >
              <div class="option-block">
                <p class="option-title">Subtitle</p>
                <input
                  v-model="welcomeSubtitle"
                  class="manual-input manual-input--full"
                  :placeholder="welcomeSubtitlePlaceholder"
                  spellcheck="false"
                />
              </div>
              <div class="option-block">
                <p class="option-title">Card message (hs-agent-msg)</p>
                <p class="option-hint-text">
                  Set different text for online vs away/offline.
                </p>
                <div class="ws-list ws-list--dense">
                  <div>
                    <p class="option-title option-title--micro">Online</p>
                    <textarea
                      v-model="homeCardMessageOnline"
                      class="manual-textarea"
                      rows="2"
                      :placeholder="homeCardOnlinePlaceholder"
                    />
                  </div>
                  <div>
                    <p class="option-title option-title--micro">
                      Away / offline
                    </p>
                    <textarea
                      v-model="homeCardMessageOffline"
                      class="manual-textarea"
                      rows="2"
                      :placeholder="homeCardOfflinePlaceholder"
                    />
                  </div>
                </div>
              </div>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'change_settings'"
              @click="toggleSection('change_settings')"
            >
              <span class="section-left">
                <span class="i-lucide-settings-2 section-icon" />
                <span class="section-name">Change settings</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div
              v-if="activeSection === 'change_settings'"
              class="section-content"
            >
              <label class="switch-row">
                <input v-model="showWelcomeScreen" type="checkbox" />
                <span>Show welcome screen</span>
              </label>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'custom_links'"
              @click="toggleSection('custom_links')"
            >
              <span class="section-left">
                <span class="i-lucide-link section-icon" />
                <span class="section-name">Custom links</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div
              v-if="activeSection === 'custom_links'"
              class="section-content"
            >
              <div class="option-block">
                <p class="option-title">Social media</p>
                <p class="option-hint-text">
                  Add icon links to WhatsApp, Telegram, etc. They appear as
                  round icon buttons below the card.
                </p>
                <div class="ws-list">
                  <div
                    v-for="(s, idx) in socialLinks"
                    :key="idx"
                    class="ws-social-row"
                  >
                    <select v-model="s.type" class="ws-select">
                      <option value="whatsapp">WhatsApp</option>
                      <option value="telegram">Telegram</option>
                      <option value="instagram">Instagram</option>
                      <option value="facebook">Facebook</option>
                      <option value="x">X</option>
                      <option value="youtube">YouTube</option>
                    </select>
                    <input
                      v-model="s.url"
                      class="manual-input ws-url-input"
                      placeholder="https://wa.me/..."
                      @input="pushSettingsToPreview"
                    />
                    <button
                      class="ws-icon-btn"
                      type="button"
                      @click="removeSocialLink(idx)"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <button
                  class="ghost-btn ghost-btn--top-gap"
                  type="button"
                  @click="addSocialLink"
                >
                  + Add social link
                </button>
              </div>

              <div class="option-block option-block--md-gap">
                <p class="option-title">Direct links</p>
                <p class="option-hint-text">
                  Appear as clickable cards with label + arrow below the agent
                  card.
                </p>
                <div class="ws-list">
                  <div
                    v-for="(l, idx) in directLinks"
                    :key="idx"
                    class="ws-direct-row"
                  >
                    <input
                      v-model="l.label"
                      class="ws-label-input manual-input"
                      :placeholder="directLinkLabelPlaceholder"
                      @input="pushSettingsToPreview"
                    />
                    <input
                      v-model="l.url"
                      class="manual-input ws-url-input"
                      placeholder="https://..."
                      @input="pushSettingsToPreview"
                    />
                    <button
                      class="ws-icon-btn"
                      type="button"
                      @click="removeDirectLink(idx)"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <button
                  class="ghost-btn ghost-btn--top-gap"
                  type="button"
                  @click="addDirectLink"
                >
                  + Add link
                </button>
              </div>

              <div class="option-block">
                <p class="option-title">Welcome link colors</p>
                <div class="color-grid">
                  <div class="color-field">
                    <label>Card bg</label>
                    <div class="color-input">
                      <input
                        v-model="welcomeLinkCardBg"
                        class="manual-input"
                        spellcheck="false"
                      />
                      <button
                        class="color-swatch-btn"
                        type="button"
                        @click="
                          openColorPicker(
                            'welcomeLinkCardBg',
                            welcomeLinkCardBg
                          )
                        "
                      >
                        <span
                          class="swatch-square"
                          :style="{ background: welcomeLinkCardBg }"
                        />
                        <span class="i-lucide-chevron-down size-4" />
                      </button>
                    </div>
                  </div>
                  <div class="color-field">
                    <label>Text</label>
                    <div class="color-input">
                      <input
                        v-model="welcomeLinkCardText"
                        class="manual-input"
                        spellcheck="false"
                      />
                      <button
                        class="color-swatch-btn"
                        type="button"
                        @click="
                          openColorPicker(
                            'welcomeLinkCardText',
                            welcomeLinkCardText
                          )
                        "
                      >
                        <span
                          class="swatch-square"
                          :style="{ background: welcomeLinkCardText }"
                        />
                        <span class="i-lucide-chevron-down size-4" />
                      </button>
                    </div>
                  </div>
                  <div class="color-field">
                    <label>Icon</label>
                    <div class="color-input">
                      <input
                        v-model="welcomeLinkIcon"
                        class="manual-input"
                        spellcheck="false"
                      />
                      <button
                        class="color-swatch-btn"
                        type="button"
                        @click="
                          openColorPicker('welcomeLinkIcon', welcomeLinkIcon)
                        "
                      >
                        <span
                          class="swatch-square"
                          :style="{ background: welcomeLinkIcon }"
                        />
                        <span class="i-lucide-chevron-down size-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div class="save-row">
          <NextButton
            class="lc-save-btn"
            type="button"
            :label="saveButtonLabel"
            :is-loading="uiFlags.isUpdating"
            :disabled="uiFlags.isUpdating"
            @click="save"
          />
        </div>
      </section>

      <section class="builder-preview-panel">
        <header class="preview-header">Preview</header>
        <div class="preview-stage">
          <div class="preview-scroll-inner">
            <div class="preview-device-shell">
              <div class="preview-device-stage">
                <WidgetPreviewHost
                  :inbox="inbox"
                  :settings="liveChatwootSettings"
                  iframe-class="sdk-preview-frame"
                  empty-class="preview-empty"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
/* Full-page two-column layout (same structural pattern as Language/Pre-chat) */
.widget-builder-page {
  flex: 1;
  min-height: 0;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.widget-builder-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  align-items: stretch;
}

.builder-config-panel {
  flex: 5 1 62.5%;
  min-width: 296px;
  border: none;
  border-right: 1px solid #eeeeef;
  border-radius: 0;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.builder-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 56px;
  border-bottom: 1px solid #eeeeef;
  background: #ffffff;
}

.builder-title {
  margin: 0;
  height: 56px;
  line-height: 56px;
  padding-left: 21px;
  letter-spacing: 0.3px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.builder-desc {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
}

.builder-sections {
  padding: 32px 32px 120px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  overscroll-behavior: contain;
}

.builder-section {
  border: solid 1px #eeeeef;
  margin-bottom: 12px;
  border-radius: 4px;
  background: #fff;
  width: 100%;
  max-width: 705px;
  overflow: hidden;
}
.section-trigger {
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  min-height: 60px;
  padding: 0 17px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}
.section-trigger:hover {
  background: rgba(0, 0, 0, 0.03);
}
.section-left {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.section-icon {
  font-size: 18px;
  color: #111827;
  width: 18px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.section-name {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.section-chevron {
  color: #111827;
  font-size: 16px;
}
.section-content {
  border-top: 1px solid #eeeeef;
  padding: 16px;
}

.option-block {
  margin-top: 8px;
}
.option-block--md-gap {
  margin-top: 12px;
}
.option-title {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.option-title--micro {
  margin: 0 0 4px;
  font-size: 10px;
}
.manual-input {
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  padding: 0 12px;
  height: 38px;
  font-size: 13px;
  background: #fff;
  color: #111827;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.manual-input--full {
  width: 100%;
}
.manual-input:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.12);
}
.manual-textarea {
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 13px;
  background: #fff;
  color: #111827;
  resize: vertical;
  width: 100%;
  min-height: 76px;
  box-sizing: border-box;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  line-height: 1.5;
}
.manual-textarea:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.12);
}
.switch-row {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  margin-bottom: 8px;
  color: #111827;
}
.upload-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin: 10px 0;
}
.upload-preview {
  width: 80px;
  height: 80px;
  border: 1px dashed #bcc6d0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8fafc;
}
.upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.upload-placeholder {
  font-size: 11px;
  color: #94a3b8;
}
.upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bcc6d0;
  border-radius: 8px;
  height: 36px;
  padding: 0 14px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
}
.hidden-upload {
  display: none;
}
.ghost-btn {
  border: 1px solid #bcc6d0;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  height: 36px;
  padding: 0 12px;
  cursor: pointer;
  color: #374151;
}
.ghost-btn--top-gap {
  margin-top: 8px;
}
.ghost-btn:hover {
  background: #f5f5f5;
}
.option-hint-text {
  font-size: 12px;
  color: #6b7280;
  margin: 2px 0 6px;
  line-height: 18px;
}
.ws-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.ws-list--dense {
  gap: 10px;
}
.ws-list {
  max-height: 260px;
  overflow-y: auto;
  padding-right: 6px;
}
.ws-social-row {
  display: flex;
  gap: 6px;
  align-items: center;
}
.ws-direct-row {
  display: flex;
  gap: 6px;
  align-items: center;
}
.ws-select {
  flex: 0 0 120px;
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
  color: #111827;
  height: 38px;
  box-sizing: border-box;
}
.ws-url-input {
  flex: 1;
  min-width: 0;
  height: 38px;
  box-sizing: border-box;
  padding: 0 10px;
}
.ws-label-input {
  flex: 0 0 100px;
  height: 38px;
  box-sizing: border-box;
  padding: 0 10px;
}
.ws-input--grow {
  flex: 1;
  min-width: 0;
}
.ws-icon-btn {
  border: 1px solid #bcc6d0;
  background: #fff;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #374151;
}
.color-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 8px;
}
.color-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.color-field label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.color-input {
  display: flex;
  gap: 6px;
  align-items: center;
}
.color-swatch-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #bcc6d0;
  background: #fff;
  padding: 0 8px;
  height: 38px;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  color: #6b7280;
}
.swatch-square {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(15, 23, 42, 0.12);
}
.save-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  margin-top: auto;
  background: #fff;
  flex-shrink: 0;
}

.save-row :deep(.lc-save-btn) {
  height: 36px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  background: #0059e1 !important;
  border: 1px solid #0059e1 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

.save-row :deep(.lc-save-btn:hover) {
  background: #0047c2 !important;
  border-color: #0047c2 !important;
}

/* Preview panel */
.builder-preview-panel {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.preview-header {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  padding: 0 20px;
}

.preview-stage {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;
}

.preview-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.preview-device-shell {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.preview-device-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.sdk-preview-frame {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  text-align: center;
}

.preview-note {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 8px 0 4px;
}

.chat-page-link {
  display: block;
  margin: 0 auto 14px;
  border: none;
  background: transparent;
  color: #4384f5;
  font-size: 13px;
  cursor: pointer;
  text-align: center;
}
.widget-color-popover {
  position: fixed;
  z-index: 10000;
}
.widget-color-popover__chrome.vc-chrome {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.24);
}

/* Force split-panel independent scrolling (same behavior as Eye-catcher) */
.widget-builder-page.ws-scroll-layout {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.widget-builder-page.ws-scroll-layout > .widget-builder-layout {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.widget-builder-page.ws-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  flex: 5 1 62.5%;
}

.widget-builder-page.ws-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel
  > .builder-header {
  flex-shrink: 0;
  height: 56px;
  min-height: 56px;
}

.widget-builder-page.ws-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel
  > .save-row {
  flex-shrink: 0;
}

.widget-builder-page.ws-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel
  > .builder-sections {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}

.widget-builder-page.ws-scroll-layout
  > .widget-builder-layout
  > .builder-preview-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  flex: 3 1 37.5%;
}

.widget-builder-page.ws-scroll-layout
  > .widget-builder-layout
  > .builder-preview-panel
  > .preview-header {
  flex-shrink: 0;
  height: 56px;
  min-height: 56px;
}

.widget-builder-page.ws-scroll-layout
  > .widget-builder-layout
  > .builder-preview-panel
  > .preview-stage {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}
</style>
